import React, { useState } from 'react';
import Select, { components as SelectComponents } from 'react-select';
import ModuleOptionsModal from './ModuleOptionsModal';

const ModulesSection = ({
    data,
    updatedClient,
    components,
    toggleFrontendSection,
    toggleModuleOption,
    addDefaultImputations,
    reviewSeedPromptAnswered,
    markReviewSeedPromptAnswered,
    requiredExpenseFields,
    setExpenseFieldRequired,
    reviewMode,
    setExpenseReviewMode
}) => {
    const [showOptionsModal, setShowOptionsModal] = useState(false);
    const [selectedModuleForOptions, setSelectedModuleForOptions] = useState(null);

    // Transform components data for react-select
    const options = components.map(component => ({
        value: component._id,
        label: data?.t?.read(component.name) ?? component.name,
        component
    }));

    // Get currently selected components
    const selectedComponents = updatedClient.config.components || [];
    const selectedOptions = options.filter(option =>
        selectedComponents.findIndex(comp => comp.id === option.value) !== -1
    );

    // Handle adding/removing modules
    const handleChange = (selectedOptions) => {
        const selectedIds = selectedOptions ? selectedOptions.map(o => o.value) : [];
        const removed = selectedComponents.filter(comp => !selectedIds.includes(comp.id));
        const added = selectedOptions ? selectedOptions
            .filter(o => !selectedComponents.find(comp => comp.id === o.value))
            .map(o => o.component) : [];

        removed.forEach(comp => {
            const original = components.find(c => c._id === comp.id);
            if (original) toggleFrontendSection(original);
        });

        added.forEach(comp => {
            // Toggle module and default options together
            toggleFrontendSection(comp);
            
            // Apply default options in one batch
            if (comp.options && Array.isArray(comp.options)) {
                comp.options
                    .filter(option => option.default === true)
                    .forEach(option => toggleModuleOption(comp._id, option.name));
            }
        });
    };

    const openOptionsModal = (moduleId) => {
        setSelectedModuleForOptions(moduleId);
        setShowOptionsModal(true);
    };

    const closeOptionsModal = () => {
        setShowOptionsModal(false);
        setSelectedModuleForOptions(null);
    };

    // Custom MultiValueLabel: clickable chip with gear icon
    const MultiValueLabel = (props) => {
        const { data: selectedOption } = props;
        const component = components.find(comp => comp._id === selectedOption.value);
        const hasOptions = component?.options && Array.isArray(component.options) && component.options.length > 0;

        if (!hasOptions) return <SelectComponents.MultiValueLabel {...props} />;

        return (
            <div
                onClick={(e) => {
                    // Prevent closing or unselecting
                    if (e.target.closest('.react-select__multi-value__remove')) return;
                    e.stopPropagation();
                    openOptionsModal(selectedOption.value);
                }}
                style={{
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                }}
                title="Click to configure module options"
                className="module-label-clickable"
            >
                <SelectComponents.MultiValueLabel {...props} />
                <span
                    style={{
                        fontSize: '12px',
                        opacity: 0.7,
                        marginLeft: '2px'
                    }}
                >
                    ⚙️
                </span>
            </div>
        );
    };

    return (
        <div className="mb-2">
            <h5 className="mt-4">Modules</h5>
            <div className="row mt-1">
                <div className="col-2">Modules :</div>
                <div className="col-10">
                    <Select
                        isMulti
                        options={options}
                        value={selectedOptions}
                        onChange={handleChange}
                        placeholder="Select modules..."
                        className="react-select-container"
                        classNamePrefix="react-select"
                        closeMenuOnSelect={false}
                        menuIsOpen={showOptionsModal ? false : undefined} 
                        components={{ MultiValueLabel }}
                    />
                </div>
            </div>

            {/* Modal for module options */}
            <ModuleOptionsModal
                isOpen={showOptionsModal}
                moduleId={selectedModuleForOptions}
                moduleLabel={options.find(opt => opt.value === selectedModuleForOptions)?.label}
                selectedComponents={selectedComponents}
                components={components}
                data={data}
                toggleModuleOption={toggleModuleOption}
                addDefaultImputations={addDefaultImputations}
                reviewSeedPromptAnswered={reviewSeedPromptAnswered}
                markReviewSeedPromptAnswered={markReviewSeedPromptAnswered}
                requiredExpenseFields={requiredExpenseFields}
                setExpenseFieldRequired={setExpenseFieldRequired}
                reviewMode={reviewMode}
                setExpenseReviewMode={setExpenseReviewMode}
                onClose={closeOptionsModal}
            />
        </div>
    );
};

export default ModulesSection;
